import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { center, feature } from '@turf/turf';
import { loadAsync } from 'jszip';
import convertToWGS84 from 'rd-to-wgs84';
import { from } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { combine, parseDbf, parseShp } from 'shpjs';
import { CodaltComponent } from '../codalt.component';
import { Customer } from '../customer';
import { CustomerProject } from '../customer-project.interface';
import { LocalStorage } from '../local-storage.class';
import { ConfirmDialogService } from '../services/confirm-dialog-service/confirm-dialog.service';
import { CustomerProjectService } from '../services/customer-project/customer-project.service';
import { CustomerService } from '../services/customer/customer.service';
import { WorkareaService } from '../services/workarea/workarea.service';
import { FeaturePreviewDialogComponent } from '../shared/feature-preview-dialog/feature-preview-dialog.component';
import { Workarea } from '../workarea.class';
import { FieldOptionsService } from '../workarea/shared/field-options.service';
import { LocationService } from '../workarea/shared/location.service';

export interface ShapefileCombination {
    name: string;
    dbfData: ArrayBuffer | null;
    shpData: ArrayBuffer;
}

@Component({
    selector: 'app-import-shapefile',
    templateUrl: './import-shapefile.component.html',
    standalone: false,
})
export class ImportShapefileComponent extends CodaltComponent implements OnInit {
    public readonly workareaFields: string[] = [
        'number',
        'street',
        'zipcode',
        'housenumber',
        'place',
        'road_number',
        'comments',
    ];

    public readonly workareaFieldMeta: Record<string, { label: string; description: string }> = {
        number: { label: 'Nummer', description: 'Het nummer van het werkvak.' },
        street: { label: 'Straat', description: '' },
        zipcode: { label: 'Postcode', description: '' },
        housenumber: { label: 'Huisnummer', description: '' },
        place: { label: 'Plaats', description: '' },
        road_number: { label: 'Wegnummer', description: 'Het wegnummer, bijv. N270.' },
        comments: { label: 'Opmerkingen', description: '' },
    };

    /** Loaded from FieldOptionsService (type 'general') */
    public extraFieldOptions: Record<string, string[]> = {};
    public extraFieldLabels: Record<string, string> = {};
    public fieldOptionsLoaded = false;

    /** Known Dutch translations for extra field keys */
    private readonly extraFieldLabelTranslations: Record<string, string> = {
        location_damage: 'Schade Locatie',
        roadblock: 'Afzetting',
        execution: 'Uitvoering',
        wearlayer: 'Slijtlaag',
        genpriority: 'Prioriteit',
        waymark: 'Wegmarkering',
        damage: 'Schade',
        toplayer: 'Toplaag',
        bottomlayer: 'Onderlaag',
        interlayer: 'Tussenlaag',
    };

    private readonly fieldNameMappings = new Map<string, string>([
        ['adres', 'street'],
        ['straat', 'street'],
        ['straatnaam', 'street'],
        ['postcode', 'zipcode'],
        ['huisnummer', 'housenumber'],
        ['nummer', 'number'],
        ['nr', 'number'],
        ['id', 'number'],
        ['identificatie', 'number'],
        ['plaats', 'place'],
        ['gemeente', 'place'],
        ['stad', 'place'],
        ['wegnummer', 'road_number'],
        ['weg_nr', 'road_number'],
        ['opmerking', 'comments'],
        ['opmerkingen', 'comments'],
        ['beschrijving', 'comments'],
        ['locatie_schade', 'location_damage'],
        ['afzetting', 'roadblock'],
        ['uitvoering', 'execution'],
        ['slijtlaag', 'wearlayer'],
        ['prioriteit', 'genpriority'],
        ['wegmarkering', 'waymark'],
        ['schade', 'damage'],
        ['toplaag', 'toplayer'],
        ['onderlaag', 'bottomlayer'],
        ['tussenlaag', 'interlayer'],
    ]);

    public importForm: FormGroup;
    public customers: Customer[] = [];
    public projects: CustomerProject[] = [];
    public shapefileCombinations: ShapefileCombination[] = [];
    public selectedCombinations: ShapefileCombination[] = [];
    public allFeatures: any[] = [];
    public shapefileColumns: string[] = [];
    public featuresProcessed = false;

    /** extra field value mappings: extraKey → (shapefileValue → allowedOption) */
    public extraFieldMappings: Map<string, Map<string, string>> = new Map();
    /** unique shapefile values per extra field key */
    public extraFieldUniqueValues: Map<string, string[]> = new Map();

    public saving = false;
    public saveCount = 0;
    public saveTotal = 0;
    public errorMessage: string | null = null;

    /** Project id from the `project` query param, consumed once by the next loadProjects() call. */
    private pendingPrefillProjectId: number | null = null;

    public get extraFieldKeys(): string[] {
        return Object.keys(this.extraFieldOptions);
    }

    public get workareaMappingsFormArray(): FormArray {
        return this.importForm.get('workareaMappings') as FormArray;
    }

    public get extraMappingsFormArray(): FormArray {
        return this.importForm.get('extraMappings') as FormArray;
    }

    public get commentFieldsControl(): FormControl {
        return this.importForm.get('commentFields') as FormControl;
    }

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private fb: FormBuilder,
        private workareaService: WorkareaService,
        private customerService: CustomerService,
        private customerProjectService: CustomerProjectService,
        private dialog: MatDialog,
        private confirmDialogService: ConfirmDialogService,
        private locationService: LocationService,
        private fieldOptionsService: FieldOptionsService
    ) {
        super();
    }

    ngOnInit(): void {
        this.buildForm();

        const queryParams = this.activatedRoute.snapshot.queryParams;
        const prefillCustomerId = queryParams['customer'] ? Number(queryParams['customer']) : null;
        this.pendingPrefillProjectId = queryParams['project'] ? Number(queryParams['project']) : null;

        this.subscriptions.add(
            this.customerService.getList().subscribe(customers => {
                this.customers = customers;
                if (prefillCustomerId && customers.some(c => c.id === prefillCustomerId)) {
                    // Triggers the customer_id valueChanges handler, which loads projects
                    // and consumes pendingPrefillProjectId.
                    this.importForm.get('customer_id').setValue(prefillCustomerId);
                }
            })
        );
        this.subscriptions.add(
            this.fieldOptionsService.getOptions('general').subscribe(options => {
                this.extraFieldOptions = options as Record<string, string[]>;
                this.extraFieldLabels = Object.keys(this.extraFieldOptions).reduce(
                    (labels, key) => {
                        labels[key] = this.extraFieldLabelTranslations[key] ?? key;
                        return labels;
                    },
                    {} as Record<string, string>
                );
                this.fieldOptionsLoaded = true;
                // Rebuild extraMappings FormArray now that we know the keys
                const extraMappingControls = this.extraFieldKeys.map(() => this.fb.control(''));
                this.importForm.setControl('extraMappings', this.fb.array(extraMappingControls));
            })
        );
    }

    private buildForm(): void {
        const workareaMappingControls = this.workareaFields.map(() => this.fb.control(''));

        this.importForm = this.fb.group({
            customer_id: ['', Validators.required],
            project_id: ['', Validators.required],
            autoFillLocationFields: [false],
            workareaMappings: this.fb.array(workareaMappingControls),
            extraMappings: this.fb.array([]),
            commentFields: [[]],
        });

        this.importForm.get('customer_id').valueChanges.subscribe(customerId => {
            this.importForm.get('project_id').setValue('');
            this.projects = [];
            const prefillProjectId = this.pendingPrefillProjectId;
            this.pendingPrefillProjectId = null;
            if (customerId) {
                this.loadProjects(customerId, prefillProjectId);
            }
        });
    }

    private loadProjects(customerId: number, prefillProjectId?: number): void {
        this.subscriptions.add(
            this.customerProjectService.getProjects(false, customerId).subscribe(projects => {
                this.projects = projects ?? [];
                if (prefillProjectId && this.projects.some(p => p.id === prefillProjectId)) {
                    this.importForm.get('project_id').setValue(prefillProjectId);
                }
            })
        );
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files?.length) {
            return;
        }
        this.readZipFile(input.files[0]);
        input.value = '';
    }

    private async readZipFile(file: File): Promise<void> {
        this.shapefileCombinations = [];
        this.selectedCombinations = [];
        this.allFeatures = [];
        this.shapefileColumns = [];
        this.featuresProcessed = false;
        this.errorMessage = null;

        try {
            const arrayBuffer = await file.arrayBuffer();
            const zip = await loadAsync(arrayBuffer);
            const shpFiles: string[] = [];

            zip.forEach(relativePath => {
                if (relativePath.toLowerCase().endsWith('.shp')) {
                    shpFiles.push(relativePath);
                }
            });

            for (const shpPath of shpFiles) {
                const baseName = shpPath.replace(/\.shp$/i, '');
                const shpBuf = await zip.file(shpPath)?.async('arraybuffer');
                const dbfKey = Object.keys((zip as any).files).find(
                    f => f.toLowerCase() === baseName.toLowerCase() + '.dbf'
                );
                const dbfBuf = dbfKey ? await zip.file(dbfKey)?.async('arraybuffer') : null;

                if (shpBuf) {
                    this.shapefileCombinations.push({
                        name: baseName,
                        shpData: shpBuf,
                        dbfData: dbfBuf ?? null,
                    });
                }
            }

            if (this.shapefileCombinations.length === 0) {
                this.errorMessage = 'Geen .shp bestanden gevonden in de ZIP.';
            }
        } catch (e) {
            console.error(e);
            this.errorMessage = 'Fout bij het lezen van de ZIP: ' + (e as Error).message;
        }
    }

    private parseShapefileCombination(combination: ShapefileCombination): any[] {
        try {
            const geojson: any = combine([
                parseShp(combination.shpData),
                combination.dbfData ? parseDbf(combination.dbfData, new ArrayBuffer(0)) : [],
            ]);
            return geojson?.features ?? [];
        } catch (e) {
            console.error('Error parsing shapefile', combination.name, e);
            return [];
        }
    }

    public selectAllCombinations(): void {
        this.selectedCombinations = [...this.shapefileCombinations];
    }

    public deselectAllCombinations(): void {
        this.selectedCombinations = [];
    }

    public isCombinationSelected(combination: ShapefileCombination): boolean {
        return this.selectedCombinations.includes(combination);
    }

    public onSelectionChange(combination: ShapefileCombination, selected: boolean): void {
        if (selected) {
            if (!this.selectedCombinations.includes(combination)) {
                this.selectedCombinations.push(combination);
            }
        } else {
            this.selectedCombinations = this.selectedCombinations.filter(c => c !== combination);
        }
    }

    public processSelectedCombinations(): void {
        this.allFeatures = [];
        this.shapefileColumns = [];
        this.extraFieldUniqueValues = new Map();
        this.extraFieldMappings = new Map();
        this.featuresProcessed = false;
        this.commentFieldsControl.setValue([]);
        this.errorMessage = null;

        for (const combination of this.selectedCombinations) {
            const features = this.parseShapefileCombination(combination);
            this.allFeatures.push(...features);
        }

        if (this.allFeatures.length === 0) {
            this.errorMessage = 'Geen features gevonden in de geselecteerde shapefiles.';
            return;
        }

        const colSet = new Set<string>();
        this.allFeatures.forEach(f => {
            if (f.properties) {
                Object.keys(f.properties).forEach(k => colSet.add(k));
            }
        });
        this.shapefileColumns = Array.from(colSet);
        this.featuresProcessed = true;

        this.applyAutomaticFieldMappings();
    }

    private applyAutomaticFieldMappings(): void {
        this.workareaFields.forEach((field, index) => {
            const matchedCol = this.shapefileColumns.find(col => {
                const lower = col.toLowerCase();
                return this.fieldNameMappings.get(lower) === field || lower === field;
            });
            if (matchedCol) {
                this.workareaMappingsFormArray.at(index).setValue(matchedCol);
            }
        });

        this.extraFieldKeys.forEach((extraKey, index) => {
            const matchedCol = this.shapefileColumns.find(col => {
                const lower = col.toLowerCase();
                return this.fieldNameMappings.get(lower) === extraKey || lower === extraKey;
            });
            if (matchedCol) {
                this.extraMappingsFormArray.at(index).setValue(matchedCol);
                this.onExtraColumnSelected(extraKey, index, matchedCol);
            }
        });
    }

    public onExtraColumnSelected(extraKey: string, _index: number, column: string): void {
        if (!column) {
            this.extraFieldUniqueValues.delete(extraKey);
            this.extraFieldMappings.delete(extraKey);
            return;
        }

        const uniqueValues = new Set<string>();
        this.allFeatures.forEach(f => {
            const val = f.properties?.[column];
            if (val !== null && val !== undefined && val !== '') {
                uniqueValues.add(String(val));
            }
        });
        this.extraFieldUniqueValues.set(extraKey, Array.from(uniqueValues));

        const allowedOptions = this.extraFieldOptions[extraKey];
        const valueMap = new Map<string, string>();
        uniqueValues.forEach(shpVal => {
            const match = allowedOptions.find(opt => opt.toLowerCase() === shpVal.toLowerCase());
            valueMap.set(shpVal, match ?? '');
        });
        this.extraFieldMappings.set(extraKey, valueMap);
    }

    public setExtraValueMapping(extraKey: string, shpValue: string, mappedValue: string): void {
        if (!this.extraFieldMappings.has(extraKey)) {
            this.extraFieldMappings.set(extraKey, new Map());
        }
        this.extraFieldMappings.get(extraKey).set(shpValue, mappedValue);
    }

    private isValidRDCoordinate(x: number, y: number): boolean {
        return x >= 0 && x <= 290000 && y >= 290000 && y <= 630000;
    }

    private convertCoordinatesIfNeeded(coordinates: number[]): number[] {
        const [x, y] = coordinates;
        if (this.isValidRDCoordinate(x, y)) {
            const result = convertToWGS84(x, y);
            if (!result.error) {
                return [result.lon, result.lat];
            }
        }
        return coordinates;
    }

    private convertRingCoordinates(ring: number[][]): number[][] {
        return ring.map(coord => this.convertCoordinatesIfNeeded(coord));
    }

    private convertGeometryCoordinates(geometry: any): any {
        if (!geometry) {
            return geometry;
        }
        const type: string = geometry.type;
        let coordinates = geometry.coordinates;

        if (type === 'Point') {
            coordinates = this.convertCoordinatesIfNeeded(coordinates);
        } else if (type === 'LineString' || type === 'MultiPoint') {
            coordinates = coordinates.map((c: number[]) => this.convertCoordinatesIfNeeded(c));
        } else if (type === 'Polygon' || type === 'MultiLineString') {
            coordinates = coordinates.map((ring: number[][]) => this.convertRingCoordinates(ring));
        } else if (type === 'MultiPolygon') {
            coordinates = coordinates.map((poly: number[][][]) =>
                poly.map((ring: number[][]) => this.convertRingCoordinates(ring))
            );
        }
        return { ...geometry, coordinates };
    }

    private getLatLngFromGeometry(geometry: any): { lat: number | null; lng: number | null; geom_geojson: any } {
        if (!geometry) {
            return { lat: null, lng: null, geom_geojson: null };
        }

        const g = this.convertGeometryCoordinates(geometry);

        if (g.type === 'Point') {
            return { lat: g.coordinates[1], lng: g.coordinates[0], geom_geojson: null };
        }

        if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
            try {
                const c = center(feature(g));
                return { lat: c.geometry.coordinates[1], lng: c.geometry.coordinates[0], geom_geojson: g };
            } catch {
                return { lat: null, lng: null, geom_geojson: g };
            }
        }

        if (g.type === 'LineString' || g.type === 'MultiLineString') {
            try {
                const c = center(feature(g));
                return { lat: c.geometry.coordinates[1], lng: c.geometry.coordinates[0], geom_geojson: null };
            } catch {
                return { lat: null, lng: null, geom_geojson: null };
            }
        }

        return { lat: null, lng: null, geom_geojson: null };
    }

    private createWorkarea(feature: any, customerId: number, projectId: number): Workarea {
        const workarea = new Workarea();
        workarea.status_id = 101;
        workarea.customer_id = customerId;
        workarea.customer_project_id = projectId;
        workarea.user_id = LocalStorage.getUser()?.id;

        this.workareaFields.forEach((field, index) => {
            const shpProp = this.workareaMappingsFormArray.at(index)?.value;
            if (shpProp && feature.properties?.[shpProp] !== undefined) {
                (workarea as any)[field] = feature.properties[shpProp];
            }
        });

        this.extraFieldKeys.forEach((extraKey, index) => {
            const shpProp = this.extraMappingsFormArray.at(index)?.value;
            if (shpProp && feature.properties?.[shpProp] !== undefined) {
                const shapefileValue = String(feature.properties[shpProp]);
                const mappedValue = this.extraFieldMappings.get(extraKey)?.get(shapefileValue) ?? null;
                if (mappedValue) {
                    workarea.extra[extraKey] = mappedValue;
                }
            }
        });

        const { lat, lng, geom_geojson } = this.getLatLngFromGeometry(feature.geometry);
        // Unusable geometry gives null here, so keep whatever the column mapping produced.
        if (lat !== null && lng !== null) {
            workarea.lat = lat;
            workarea.lng = lng;
        } else {
            workarea.lat = workarea.lat ?? null;
            workarea.lng = workarea.lng ?? null;
        }
        workarea.geom_geojson = geom_geojson;

        // Append comment fields block
        const selectedCommentFields: string[] = this.commentFieldsControl.value ?? [];
        if (selectedCommentFields.length > 0) {
            const lines = selectedCommentFields
                .filter(
                    col =>
                        feature.properties?.[col] !== undefined &&
                        feature.properties?.[col] !== null &&
                        feature.properties?.[col] !== ''
                )
                .map(col => `${col}: ${feature.properties[col]}`);
            if (lines.length > 0) {
                const block = lines.join('\n');
                workarea.comments = workarea.comments ? `${workarea.comments}\n\n${block}` : block;
            }
        }

        return workarea;
    }

    private fillLocationForWorkarea(workarea: Workarea): Promise<void> {
        return new Promise<void>(resolve => {
            if (!workarea.lat || !workarea.lng) {
                resolve();
                return;
            }
            this.locationService
                .getLocations(workarea.lat, workarea.lng, 'ImportShapefileComponent.fillLocationForWorkarea')
                .subscribe({
                    next: (results: any[]) => {
                        if (results?.length) {
                            const components: any[] = results[0].address_components ?? [];
                            const get = (types: string[]) =>
                                components.find((c: any) => types.some(t => c.types.includes(t)))?.long_name ?? '';

                            if (!workarea.street) {
                                workarea.street = get(['route']);
                            }
                            if (!workarea.housenumber) {
                                workarea.housenumber = get(['street_number']);
                            }
                            if (!workarea.zipcode) {
                                workarea.zipcode = get(['postal_code']);
                            }
                            if (!workarea.place) {
                                workarea.place = get(['locality', 'sublocality', 'administrative_area_level_2']);
                            }
                        }
                        resolve();
                    },
                    error: () => resolve(),
                });
        });
    }

    public async importWorkareas(): Promise<void> {
        if (this.importForm.invalid) {
            this.importForm.markAllAsTouched();
            return;
        }
        if (this.allFeatures.length === 0) {
            this.errorMessage = 'Geen features om te importeren. Verwerk eerst de shapefile combinaties.';
            return;
        }

        const confirmed = await this.confirmDialogService
            .confirm(
                'Werkvakken importeren',
                `Weet u zeker dat u ${this.allFeatures.length} werkvak(ken) wilt importeren?`,
                'Importeren',
                'Annuleren'
            )
            .then(() => true)
            .catch(() => false);

        if (!confirmed) {
            return;
        }

        const customerId = Number(this.importForm.get('customer_id').value);
        const projectId = Number(this.importForm.get('project_id').value);
        const autoFill = this.importForm.get('autoFillLocationFields').value as boolean;

        const workareas: Workarea[] = this.allFeatures.map(f => this.createWorkarea(f, customerId, projectId));

        // Workareas without coordinates are rejected, so stop before the batch is half imported.
        const withoutCoordinates = workareas.filter(w => w.lat === null || w.lng === null);
        if (withoutCoordinates.length > 0) {
            this.errorMessage =
                `${withoutCoordinates.length} van de ${workareas.length} werkvak(ken) hebben geen bruikbare coordinaten, ` +
                'daardoor kan er geen locatie bepaald worden. Controleer de shapefile en probeer het opnieuw.';
            return;
        }

        if (autoFill) {
            for (const workarea of workareas) {
                await this.fillLocationForWorkarea(workarea);
            }
        }

        this.saving = true;
        this.saveCount = 0;
        this.saveTotal = workareas.length;
        this.errorMessage = null;

        from(workareas)
            .pipe(concatMap(workarea => from(this.workareaService.saveWorkarea(workarea))))
            .subscribe({
                next: () => {
                    this.saveCount++;
                },
                error: err => {
                    console.error('Fout bij opslaan werkvak', err);
                    this.saving = false;
                    this.errorMessage = 'Er is een fout opgetreden bij het opslaan van een werkvak.';
                },
                complete: () => {
                    this.saving = false;
                    this.workareaService.resetLastCustomerId();
                    this.router.navigate(['/', this.Routenames.customers, customerId], {
                        queryParams: { project: projectId },
                    });
                },
            });
    }

    public openFeaturePreviewDialog(): void {
        this.dialog.open(FeaturePreviewDialogComponent, {
            data: { features: this.allFeatures, columns: this.shapefileColumns },
            width: '90vw',
            maxWidth: '90vw',
        });
    }
}
